import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Button,
  CircularProgress,
  TextField,
  Divider
} from '@mui/material';
import { Close, WhatsApp, Email, Send, ArrowBack, CheckCircle, Error as ErrorIcon, EmailOutlined, SendOutlined } from '@mui/icons-material';
import { config } from '../../services/config';
import { esEmailValido } from "../../utils/email";
import { toast } from "react-toastify";


const TicketShareModal = ({ open, setShareModalOpen, onClose, ticketId, eventoNombre, ticketTipo, ticketName, itemType = 'ticket' }) => {
  const isVoucher = itemType === 'voucher';
  const itemLabel = isVoucher ? 'voucher' : 'ticket';
  const [emailAddress, setEmailAddress] = useState("");
  const [shareOption, setShareOption] = useState(null);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [sendError, setSendError] = useState("");
  const apiOrigin = config.URL_API;

  const handleOpen = () => setShareModalOpen(true);
  const handleClose = () => setShareModalOpen(false);

  const [generandoLink, setGenerandoLink] = useState(false);

  const handleWhatsAppShare = async () => {
    if (generandoLink) return;
    setGenerandoLink(true);
    try {
      const ruta = isVoucher ? `/vouchers/${ticketId}/share-link` : `/tickets/${ticketId}/share-link`;
      const response = await fetch(`${apiOrigin}${ruta}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Error al generar enlace compartible');
      const data = await response.json();
      const mensaje = `¡Hola! Te comparto el ${itemLabel} para ${eventoNombre}.\n\nPodés verlo y descargarlo desde este enlace: ${data.shareableLink}`;
      window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(mensaje)}`, '_blank');
      setTimeout(() => onClose(), 500);
    } catch (error) {
      console.error('Error al compartir por WhatsApp:', error);
      toast.error('No se pudo generar el enlace para compartir. Intentá de nuevo.');
    } finally {
      setGenerandoLink(false);
    }
  };

  const handleEmailStep = () => {
    setShareOption('email');
  };

  const handleSendEmail = async () => {
    if (!esEmailValido(emailAddress)) {
      setSendError('Ingresá un correo válido, por ejemplo nombre@dominio.com.');
      setSendResult('error');
      return;
    }

    try {
      setSending(true);
      setSendResult(null);
      setSendError("");

      // el backend verifica que el item sea del usuario, genera el PDF y arma asunto/cuerpo
      const sharePath = isVoucher ? '/api/vouchers/share-email' : '/api/tickets/share-email';
      const payload = isVoucher
        ? { voucherId: Number(ticketId), recipient: emailAddress.trim() }
        : { ticketId: Number(ticketId), recipient: emailAddress.trim() };

      const response = await fetch(`${apiOrigin}${sharePath}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        const err = new Error(data.message || 'Error al enviar el correo');
        err.mostrar = true;
        throw err;
      }

      setSendResult('success');

      setTimeout(() => {
        onClose();
        setSendResult(null);
        setShareOption(null);
      }, 2000);

    } catch (error) {
      console.error('Error al enviar correo:', error);
      setSendError(error.mostrar ? error.message : '');
      setSendResult('error');
    } finally {
      setSending(false);
    }
  };

  const handleBackToOptions = () => {
    setShareOption(null);
    setSendResult(null);
    setSendError("");
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: '10px'
          }
        }}
      >
        <DialogTitle sx={{}}>
          <Typography sx={{ fontSize: "18px" }} align="center" fontWeight={600}>
            {isVoucher ? 'Compartir Voucher' : 'Compartir Ticket'}
          </Typography>
          <IconButton
            edge="end"
            color="inherit"
            onClick={onClose}
            aria-label="close"
            sx={{
              position: 'absolute',
              top: 8,
              /* right: 8 */
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ padding: "20px !important" }}>
          {!shareOption ? (
            <>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 4, textAlign: 'center' }}>
                Selecciona cómo quieres compartir tu {itemLabel} <br />{eventoNombre ? `para ${eventoNombre}` : ''}
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: "center", alignItems: "center", gap: 3, mb: 4 }}>
                <Box
                  component="button"
                  type="button"
                  onClick={handleWhatsAppShare}
                  disabled={generandoLink}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textDecoration: 'none',
                    color: 'inherit',
                    cursor: generandoLink ? 'wait' : 'pointer',
                    '&:hover': { textDecoration: 'none', color: '#25D366' }
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: 'center', justifyContent: "center", width: 50, height: 50, mb: 1, borderRadius: '100%', backgroundColor: "#E6E6FF" }}>
                    {generandoLink ? <CircularProgress size={26} sx={{ color: '#25D366' }} /> : <WhatsApp sx={{ fontSize: "28px" }} />}
                  </Box>
                  <Typography variant="body2">{generandoLink ? 'Generando enlace...' : 'WhatsApp'}</Typography>
                </Box>

                <Box
                  component="button"
                  onClick={handleEmailStep}
                  disabled={generandoLink}
                  sx={{
                    opacity: generandoLink ? 0.5 : 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textDecoration: 'none',
                    color: 'inherit',
                    '&:hover': { textDecoration: 'none', color: '#E04D3B' }
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: 'center', justifyContent: "center", width: 50, height: 50, mb: 1, borderRadius: '100%', backgroundColor: "#E6E6FF" }}>
                    <EmailOutlined sx={{ fontSize: "28px" }} />
                  </Box>
                  <Typography variant="body2">Email</Typography>
                </Box>
              </Box>

              {/* <Box sx={{ mt: 3, backgroundColor: '#f5f5f5', borderRadius: 1, p: 2 }}>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', textAlign: 'center' }}>
                  El PDF del ticket se descargará automáticamente para que lo tengas disponible.
                </Typography>
              </Box> */}
            </>
          ) : shareOption === 'email' && !sendResult ? (
            <>
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                  El sistema enviará un correo desde la cuenta oficial de PassGo con tu {itemLabel} adjunto.
                </Typography>

                <TextField
                  label="Correo del destinatario"
                  fullWidth
                  margin="normal"
                  value={emailAddress}
                  onChange={(e) => setEmailAddress(e.target.value)}
                  required
                  sx={{
                    fontFamily: "Bricolage Grotesque",
                    mb: 2,
                    '& .MuiInputBase-input-MuiOutlinedInput-input':{
                      fontFamily: "Bricolage Grotesque",
                    },
                    '& label.Mui-focused': {
                      color: '#5656FF',
                      fontFamily:"Bricolage Grotesque"
                    },
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: '#B2BAC2',
                      },
                      '&:hover fieldset': {
                        borderColor: '#5656ff',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#5656ff',
                      },
                    }
                  }}
                />
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: "10px" }}>
                <button
                  type="button"
                  onClick={handleBackToOptions}
                  style={{
                    border: "1px solid #5656FF",
                    color: "#5656FF",
                    padding: "5px 10px",
                    borderRadius: "4px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: window.innerWidth <= 576 ? "13px" : "14px",
                    width: "100%"
                  }}
                >
                  <ArrowBack /> Volver
                </button>

                <button
                  type="button"
                  disabled={sending || !emailAddress}
                  onClick={handleSendEmail}
                  style={{
                    backgroundColor: sending || !emailAddress ? "#CACACA" : "#5656ff",
                    color: "white",
                    padding: "5px 10px",
                    borderRadius: "4px",
                    cursor: sending || !emailAddress ? "default" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: window.innerWidth <= 576 ? "13px" : "14px",
                    width: "100%"
                  }}
                >
                  {sending ? (
                    <>
                      <CircularProgress size={16} sx={{ color: '#5656FF', marginRight: "4px" }} />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <SendOutlined style={{ marginRight: "4px" }} />
                      Enviar correo
                    </>
                  )}
                </button>
              </Box>
            </>
          ) : shareOption === 'email' && sendResult && (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              {sendResult === 'success' ? (
                <>
                  <CheckCircle sx={{ fontSize: 48, color: 'green', mb: 2 }} />
                  <Typography variant="h6" sx={{ mb: 1 }}>¡Correo enviado con éxito!</Typography>
                  <Typography variant="body2" color="textSecondary">
                    El {itemLabel} ha sido enviado a {emailAddress}
                  </Typography>
                </>
              ) : (
                <>
                  <ErrorIcon sx={{ fontSize: 48, color: 'red', mb: 2 }} />
                  <Typography variant="h6" sx={{ mb: 1 }}>Error al enviar</Typography>
                  <Typography variant="body2" color="textSecondary">
                    {sendError || 'Por favor verifica la dirección de correo e inténtalo de nuevo.'}
                  </Typography>
                  <Button
                    color="primary"
                    onClick={handleBackToOptions}
                    sx={{ mt: 2 }}
                  >
                    Volver
                  </Button>
                </>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TicketShareModal;